import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ALL_PERMISSIONS, DEFAULT_ROLE_PERMISSIONS, PERMISSIONS, type Permission } from '@wms/shared';
import { apiFetch, ApiError } from '../../lib/api';
import { useElevatedFetch } from '../../lib/elevation';
import { permissionLabel } from './permission-picker';

interface RoleRow {
  readonly id: string;
  readonly code: string;
  readonly nameKey: string;
  readonly isSystem: boolean;
  readonly permissions: string[];
}

export function RolesPage(): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const elevatedFetch = useElevatedFetch();
  const { data } = useQuery({
    queryKey: ['roles'],
    queryFn: () => apiFetch<{ items: RoleRow[] }>('/roles'),
  });

  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [draftPermissions, setDraftPermissions] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newCode, setNewCode] = useState('');

  const startEdit = (role: RoleRow): void => {
    setEditingRoleId(role.id);
    setDraftPermissions(new Set(role.permissions));
  };

  const togglePermission = (code: Permission): void => {
    setDraftPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const savePermissions = async (): Promise<void> => {
    if (!editingRoleId) return;
    setError(null);
    try {
      await elevatedFetch(
        PERMISSIONS.USER_MANAGE,
        `/roles/${editingRoleId}/permissions`,
        {
          permissionCodes: [...draftPermissions],
        },
        'PUT',
      );
      setEditingRoleId(null);
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
    } catch (err) {
      if (!(err instanceof Error && err.message === 'elevation_cancelled')) {
        setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
      }
    }
  };

  const create = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    try {
      // 自定义角色没有预置翻译文案，nameKey 直接落地为角色代码本身；
      // UI 渲染角色名时用 t(nameKey, code) 做 fallback（见下方渲染处），
      // 找不到翻译 key 时原样显示代码，不会出现"[missing key]"。
      await elevatedFetch(PERMISSIONS.USER_MANAGE, '/roles', { code: newCode, nameKey: newCode });
      setShowCreate(false);
      setNewCode('');
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
    } catch (err) {
      if (!(err instanceof Error && err.message === 'elevation_cancelled')) {
        setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
      }
    }
  };

  return (
    <div>
      <div className="row row--between">
        <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('users.rolesTitle')}</h1>
        <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
          {t('users.createRole')}
        </button>
      </div>
      {error && <p className="field-error">{error}</p>}

      {data?.items.map((role) => (
        <section key={role.id} className="section">
          <div className="row row--between">
            <h2 style={{ margin: 0 }}>
              {t(role.nameKey, role.code)}{' '}
              {role.isSystem && (
                <span className="badge badge--muted">{t('users.systemRoleReadonly')}</span>
              )}
            </h2>
            {!role.isSystem && editingRoleId !== role.id && (
              <button className="btn btn--ghost btn--sm" onClick={() => startEdit(role)}>
                {t('users.permissionMatrix')}
              </button>
            )}
          </div>

          {editingRoleId === role.id ? (
            <>
              {/* 【用户设计】角色权限矩阵的两个一键预设：管理人员/普通人员。
                  点击整组覆盖当前草稿，之后仍可逐项增删 */}
              <div className="row" style={{ gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setDraftPermissions(new Set(DEFAULT_ROLE_PERMISSIONS.EMPLOYEE))}
                >
                  {t('users.presetEmployee')}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setDraftPermissions(new Set(DEFAULT_ROLE_PERMISSIONS.MANAGER))}
                >
                  {t('users.presetManager')}
                </button>
              </div>
              <div className="permission-matrix">
                {ALL_PERMISSIONS.map((p) => (
                  <label key={p} title={p}>
                    <input
                      type="checkbox"
                      checked={draftPermissions.has(p)}
                      onChange={() => togglePermission(p)}
                    />
                    {permissionLabel(t, p)}
                  </label>
                ))}
              </div>
              <div className="row" style={{ marginTop: 'var(--space-3)' }}>
                <button className="btn btn--ghost" onClick={() => setEditingRoleId(null)}>
                  {t('app.cancel')}
                </button>
                <button className="btn btn--primary" onClick={() => void savePermissions()}>
                  {t('app.save')}
                </button>
              </div>
            </>
          ) : (
            <div className="permission-matrix">
              {role.permissions.map((p) => (
                <span key={p} className="badge badge--success" title={p}>
                  {permissionLabel(t, p)}
                </span>
              ))}
            </div>
          )}
        </section>
      ))}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <form
            className="modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => void create(e)}
          >
            <h2>{t('users.createRole')}</h2>
            <label className="field">
              <span>{t('users.roleName')}</span>
              <input
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                required
              />
            </label>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setShowCreate(false)}>
                {t('app.cancel')}
              </button>
              <button type="submit" className="btn btn--primary">
                {t('app.confirm')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
